import { Injectable } from '@angular/core';
import { firstValueFrom, from, map, shareReplay } from 'rxjs';
import { Agent, AgentKeys } from '@ag-common-lib/public-api';
import { AgentSearchService, AgentService, DynamicListsGroupService, DynamicSegmentsGroupService } from 'ag-common-svc/public-api';
import {
  DynamicListsGroupKeys,
  DynamicSegmentsGroupKeys,
  FilterGroup,
  NotificationOptInGroupType,
} from '@ag-common-lib/lib';
import { checkIsMeetFilterCriteria } from 'ag-common-svc/lib/utils/filter-builder-converter.utils';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { AttendeesListByConferencePipe } from '../../dynamic-segments-group/pipes/attendees-list-by-conference.pipe';

@Injectable()
export class NotificationsSchedulerCheckedRecipientsGridService {
  private readonly agents$ = from(
    this.agentSearchService.search({ sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }] }),
  ).pipe(map(response => (response?.data ?? []) as Agent[]));

  constructor(
    private agentSearchService: AgentSearchService,
    private dynamicListsGroupService: DynamicListsGroupService,
    private dynamicSegmentsGroupService: DynamicSegmentsGroupService,
    private attendeesListByConferencePipe: AttendeesListByConferencePipe,
  ) {}

  getRecipientDbIdsByLists(listDbIds: string[]) {
    return this.dynamicListsGroupService.lists$.pipe(
      map(lists =>
        lists.reduce(
          (acc, item) =>
            listDbIds.includes(item?.[BaseModelKeys.dbId])
              ? [...acc, ...item?.[DynamicListsGroupKeys.recipientsDbIds]]
              : acc,
          [],
        ),
      ),
      map(recipientIds => recipientIds ?? []),
      shareReplay(1),
    );
  }

  async getRecipientDbIdsByChannel(channels: string[]) {
    if (!channels?.length) {
      return [];
    }

    // One ask. The batching this replaces existed because Firestore caps
    // array-contains-any at ten values; notification_channels is a text[] column, so
    // membership against the whole set is a single predicate.
    const response = await this.agentSearchService.search({
      filter: [AgentKeys.notificationChannels, 'anyof', channels],
      sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }],
    });

    return (response?.data ?? []).map(({ dbId }) => dbId);
  }

  async getRecipientDbIdsBySegments(segmentDbIds: string[]) {
    const promises: Promise<any[]>[] = [];

    if (!segmentDbIds?.length) {
      return await Promise.resolve([]);
    }

    const segments = await firstValueFrom(
      this.dynamicSegmentsGroupService.segments$.pipe(map(segments => segments ?? [])),
    );

    const filteredSegments = segments.filter(segment => segmentDbIds?.includes(segment?.[BaseModelKeys.dbId]));
    filteredSegments.forEach(segment => {
      const type = segment?.[DynamicSegmentsGroupKeys.recipientsCollection];
      const filterExpression = segment?.[DynamicSegmentsGroupKeys.filterExpression];

      if (type === NotificationOptInGroupType.agent) {
        const recipientDbIds = this.getRecipientDbIdsByFilter(filterExpression);
        promises.push(recipientDbIds);
      }

      if (type === NotificationOptInGroupType.registrant) {
        const conferenceDbId = segment?.[DynamicSegmentsGroupKeys.collectionEventDbId];
        const recipientsFromConferenceDbIds = this.getRecipientsFromConference(conferenceDbId, filterExpression);
        promises.push(recipientsFromConferenceDbIds);
      }
    });

    return await Promise.all(promises).then(items => items.flat(1));
  }

  async getRecipientsFromConference(conferenceDbId: string, filterExpression: FilterGroup) {
    const agentsDbIds = [];

    if (!conferenceDbId?.length) {
      return [];
    }

    const attendees = await firstValueFrom(this.attendeesListByConferencePipe.transform(conferenceDbId));
    const attendeesDbIds = attendees.map((attendee) => attendee?.[BaseModelKeys.dbId]);

    if (!filterExpression) {
      agentsDbIds.push(attendeesDbIds);
    }

    attendees.forEach(attendee => {
      const isMeetFilterCriteria = checkIsMeetFilterCriteria(attendee, filterExpression);
      const attendeeDbId = attendee?.[BaseModelKeys.dbId];

      if (isMeetFilterCriteria) {
        agentsDbIds.push(attendeeDbId);
      }
    });

    return agentsDbIds;
  }

  async getRecipientDbIdsByFilter(filterExpression: FilterGroup) {
    const results = [];
    const agents = await firstValueFrom(this.agents$);
    agents.forEach(agent => {
      const isMeetFilterCriteria = checkIsMeetFilterCriteria(agent, filterExpression);

      if (isMeetFilterCriteria) {
        const agentDbId = agent?.[BaseModelKeys.dbId];
        results.push(agentDbId);
      }
    });
    return results;
  }
}
